import { getCurrentInstance, computed, shallowRef, unref, watchEffect, watch, toRaw } from 'vue'
import { PINNED_GROUP_KEY } from './use-pinned'

const NS = 'side-menu-v3'
const getTreeNodeKey = (item) => item?.key ?? item?.id

export const toArray = (val) => (Array.isArray(val) ? val : [])

export const isEmpty = (val) =>
  (!val && val !== 0) ||
  (Array.isArray(val) && val.length === 0) ||
  (typeof val === 'object' && val !== null && !Array.isArray(val) && !Object.keys(val).length)

export const resolve = (obj, ...params) => (typeof obj === 'function' ? obj(...params) : obj)

const getPropsData = (props) => props.menus ?? []

// Module-level constant — shared across all SideMenuSub instances (no per-instance allocation).
// Implements max-height animation, ported from ZmCollapseTransition.
export const collapseTransition = {
  onBeforeEnter(el) {
    el.dataset.oldPaddingTop = el.style.paddingTop
    el.dataset.oldPaddingBottom = el.style.paddingBottom
    el.style.maxHeight = '0'
    el.style.paddingTop = '0'
    el.style.paddingBottom = '0'
  },
  onEnter(el) {
    requestAnimationFrame(() => {
      el.dataset.oldOverflow = el.style.overflow
      el.style.maxHeight = el.scrollHeight ? `${el.scrollHeight}px` : '0'
      el.style.paddingTop = el.dataset.oldPaddingTop
      el.style.paddingBottom = el.dataset.oldPaddingBottom
      el.style.overflow = 'hidden'
    })
  },
  onAfterEnter(el) {
    el.style.maxHeight = ''
    el.style.overflow = el.dataset.oldOverflow
  },
  onBeforeLeave(el) {
    el.dataset.oldPaddingTop = el.style.paddingTop
    el.dataset.oldPaddingBottom = el.style.paddingBottom
    el.dataset.oldOverflow = el.style.overflow
    el.style.maxHeight = `${el.scrollHeight}px`
    el.style.overflow = 'hidden'
  },
  onLeave(el) {
    if (el.scrollHeight !== 0) {
      el.style.maxHeight = '0'
      el.style.paddingTop = '0'
      el.style.paddingBottom = '0'
    }
  },
  onAfterLeave(el) {
    el.style.maxHeight = ''
    el.style.overflow = el.dataset.oldOverflow
    el.style.paddingTop = el.dataset.oldPaddingTop
    el.style.paddingBottom = el.dataset.oldPaddingBottom
  },
  onLeaveCancelled(el) {
    el.style.maxHeight = ''
    el.style.overflow = el.dataset.oldOverflow
    el.style.paddingTop = el.dataset.oldPaddingTop
    el.style.paddingBottom = el.dataset.oldPaddingBottom
  }
}

export function useTree(props, activeItem, injectItems = null) {
  const instance = getCurrentInstance()
  const treeData = shallowRef({})
  let _snapshot = {}
  let _lastActiveKey = '\0'

  const processedItems = shallowRef([])

  const getMenusData = () => [
    ...(injectItems ? toArray(unref(injectItems)) : []),
    ...getPropsData(props)
  ]

  const normalizedData = computed(() => {
    const res = {}
    const walk = (parent, level, children) => {
      const parentKey = getTreeNodeKey(parent)
      if (!parentKey) return
      res[parentKey] = { children, level }
      children.forEach((item) => {
        if (item.items?.length) walk(item, level + 1, item.items)
      })
    }
    getMenusData().forEach((item) => {
      if (item.items?.length) walk(item, 0, item.items)
    })
    return res
  })

  const getAncestorKeys = (keyOrId, items = getMenusData(), ancestors = []) => {
    if (!keyOrId) return null
    for (const menu of toArray(toRaw(items))) {
      if (menu.key === keyOrId || menu.id === keyOrId) {
        return ancestors.map((a) => a.key ?? a.id).filter(Boolean)
      }
      if (menu.items?.length) {
        const found = getAncestorKeys(keyOrId, menu.items, [...ancestors, menu])
        if (found !== null) return found
      }
    }
    return null
  }

  const updateTreeData = () => {
    const nested = normalizedData.value
    const keys = Object.keys(nested)
    if (!keys.length) {
      _snapshot = {}
      treeData.value = {}
      return
    }

    const activeKey = props.activeKey
    const activeKeyChanged = activeKey !== _lastActiveKey
    _lastActiveKey = activeKey
    const ancestorKeys = activeKeyChanged ? new Set(getAncestorKeys(activeKey) ?? []) : new Set()

    const next = {}
    keys.forEach((key) => {
      const prev = _snapshot[key]
      next[key] = {
        ...nested[key],
        expanded: ancestorKeys.has(key) ? true : !!(prev?.expanded || props.defaultExpandAll)
      }
    })
    _snapshot = next
    treeData.value = next
  }

  const toggleTreeExpansion = (item, expanded) => {
    const key = getTreeNodeKey(item.item)
    const entry = _snapshot[key]
    if (!entry) return
    const next = {
      ..._snapshot,
      [key]: { ...entry, expanded: expanded ?? !entry.expanded }
    }
    _snapshot = next
    treeData.value = next
  }

  const expandKeys = (keys) => {
    if (!keys.length) return
    let changed = false
    const next = { ..._snapshot }
    keys.forEach((key) => {
      if (next[key] && !next[key].expanded) {
        next[key] = { ...next[key], expanded: true }
        changed = true
      }
    })
    if (changed) {
      _snapshot = next
      treeData.value = next
    }
  }

  const restoreToActivePath = (activeItemNode) => {
    const ancestorKeys = new Set()
    let cur = activeItemNode?.parent
    while (cur) {
      const key = getTreeNodeKey(cur.item)
      if (key) ancestorKeys.add(key)
      cur = cur.parent
    }
    let changed = false
    const next = { ..._snapshot }
    Object.keys(next).forEach((key) => {
      if (key === PINNED_GROUP_KEY) return
      const shouldExpand = ancestorKeys.has(key)
      if (next[key].expanded !== shouldExpand) {
        next[key] = { ...next[key], expanded: shouldExpand }
        changed = true
      }
    })
    if (changed) {
      _snapshot = next
      treeData.value = next
    }
  }

  // nodeKey segment: prefer item.key / item.id (stable across reorders, readable
  // for debugging, consistent with pinned tree). Fallback to `idx_<i>` only when
  // the source data omits both (the slot is still uniquely positioned in its
  // parent so this guarantees layer-uniqueness even without identifiers).
  const toStableSegment = (item, fallbackIndex) => {
    const raw = item?.key ?? item?.id
    if (raw == null || raw === '') return `idx_${fallbackIndex}`
    return String(raw)
  }

  const warnDuplicateLayerKeys = (items, level) => {
    if (!import.meta.env?.DEV) return
    const seen = new Map()
    items.forEach((it, i) => {
      const seg = toStableSegment(it, i)
      const prev = seen.get(seg)
      if (prev !== undefined) {
        console.warn(
          `[side-menu-v3] Duplicate menu key/id "${seg}" at level ${level} (indices ${prev} and ${i}). ` +
            'Each menu item at the same level should expose a unique `key` or `id`; ' +
            'otherwise focus, expand state and DOM ids will collide.'
        )
      }
      seen.set(seg, i)
    })
  }

  const createProcessedItems = (items = [], level = 0, parentKey = '', parent = undefined) => {
    warnDuplicateLayerKeys(items, level)
    const result = []
    items.forEach((item, i) => {
      const segment = toStableSegment(item, i)
      const key = parentKey ? `${parentKey}/${segment}` : segment
      const node = {
        item,
        index: i,
        level,
        key,
        parent,
        parentKey,
        expanded: _snapshot[item.key]?.expanded
      }
      if (props.activeKey === item.key) activeItem.value = node
      node.items = createProcessedItems(item.items, level + 1, key, node)
      result.push(node)
    })
    return result
  }

  const findMenuContext = (keyOrId, items = getPropsData(props), parent = null, root = null) => {
    if (!keyOrId) return null
    for (const menu of toArray(toRaw(items))) {
      const currentRoot = root ?? menu
      if (menu.key === keyOrId || menu.id === keyOrId) return { menu, parent, root: currentRoot }
      if (menu.items?.length) {
        const found = findMenuContext(keyOrId, menu.items, menu, currentRoot)
        if (found) return found
      }
    }
    return null
  }

  const findMenu = (keyOrId) => findMenuContext(keyOrId)?.menu

  const findProcessedItem = (keyOrId, list = processedItems.value) => {
    for (const item of list) {
      if (item.item.key === keyOrId || item.item.id === keyOrId) return item
      if (item.items?.length) {
        const found = findProcessedItem(keyOrId, item.items)
        if (found) return found
      }
    }
    return null
  }

  // Tab anchoring is fully owned by the reactive `getTabIndex` in
  // `<side-menu-sub>` (driven by `focusedRowKey` for roving + active item /
  // first-focusable as the initial fallback). On focus exit we only need to
  // restore the visual expansion to the active path; tabindex re-flows
  // automatically via Vue when `focusedRowKey` clears in each sub.
  //
  // Caveat: some browsers report `relatedTarget = null` for focusout fired
  // during mouse interactions even though focus actually settles INSIDE the
  // nav (e.g. clicking a non-focusable element in the same row). Naively
  // running `restoreToActivePath` on those transient focusouts re-snapshots
  // _snapshot which in turn invalidates `processedItems` and used to trigger
  // scroll-to-active jumps when expanding pinned groups. Defer the decision
  // to a microtask and consult `document.activeElement` to read the FINAL
  // focus owner before deciding to collapse non-active paths.
  const handleFocusout = (e) => {
    const nav = instance?.vnode.el?.querySelector(`.${NS}__nav`)
    if (!nav) return
    if (e.relatedTarget && nav.contains(e.relatedTarget)) return
    queueMicrotask(() => {
      const ae = document.activeElement
      if (ae && nav.contains(ae)) return
      restoreToActivePath(activeItem.value)
    })
  }

  watchEffect(() => updateTreeData())

  watch(
    treeData,
    () => {
      processedItems.value = createProcessedItems(getMenusData())
    },
    { immediate: true }
  )

  return {
    processedItems,
    toggleTreeExpansion,
    expandKeys,
    findMenu,
    findMenuContext,
    findProcessedItem,
    handleFocusout
  }
}
